import { useApi } from '@getvim/internal-vim-os-sdk/react';
import { useCallback } from 'react';
import {
  WritebackReferralCallback,
  WritebackReferralInput,
  WritebackOrderCallback,
  WritebackOrderInput,
} from './types';
import { WriteBackProvider } from '../../../../types';
import { orderAssistLogger } from '../../../../logger';

export const useSdkWriteback = (): WritebackReferralCallback => {
  const { resourceUpdater } = useApi();

  return useCallback<WritebackReferralCallback>(
    async ({ authCode, notes, targetProvider }: WritebackReferralInput) => {
      orderAssistLogger.info('Writing back referral data', { authCode, notes, targetProvider });

      const updateParams: {
        basicInformation?: { authCode?: string; notes?: string };
        targetProvider?: ReturnType<typeof convertTargetProviderToSdk>;
      } = {};

      if (
        authCode &&
        resourceUpdater.canUpdateReferral({ basicInformation: { authCode: true } }).canUpdate
      ) {
        updateParams.basicInformation = { authCode };
      }

      if (targetProvider && resourceUpdater.canUpdateReferral({ targetProvider: true }).canUpdate) {
        updateParams.targetProvider = convertTargetProviderToSdk(targetProvider);
        if (notes) {
          updateParams.basicInformation = { ...updateParams.basicInformation, notes };
        }
      }

      if (Object.keys(updateParams).length > 0) {
        await resourceUpdater.updateReferral(updateParams);
      }
    },
    [resourceUpdater],
  );
};

export const useSdkOrderWriteback = (): WritebackOrderCallback => {
  const { resourceUpdater } = useApi();

  return useCallback<WritebackOrderCallback>(
    async ({ notes, targetProvider }: WritebackOrderInput) => {
      orderAssistLogger.info('Writing back order data', { notes, targetProvider });

      const updateParams: {
        basicInformation?: { notes?: string };
        targetProvider?: ReturnType<typeof convertFacilityToSdkOrderTargetProvider>;
      } = {};

      if (targetProvider && resourceUpdater.canUpdateOrder({ targetProvider: true }).canUpdate) {
        updateParams.targetProvider = convertFacilityToSdkOrderTargetProvider(targetProvider);
      }

      if (notes && updateParams.targetProvider) {
        updateParams.basicInformation = { notes };
      }

      if (Object.keys(updateParams).length > 0) {
        await resourceUpdater.updateOrder(updateParams);
      }
    },
    [resourceUpdater],
  );
};

const convertTargetProviderToSdk = (targetProvider: WriteBackProvider) => {
  const { npi, specialty, demographics, organization, degree, organizations } = targetProvider;
  const { name, address, contact_info } = organizations?.[0] ?? organization ?? {};

  const specialties: string[] | undefined = specialty
    ?.filter(({ description }) => description)
    ?.map(({ description }) => description as string);

  return {
    npi,
    demographics,
    specialty: specialties,
    facility: {
      name,
      address,
      contact_info,
    },
    providerDegree: degree,
  };
};

const convertFacilityToSdkOrderTargetProvider = (facility: WriteBackProvider) => {
  const { npi, organizations } = facility;
  const { name, address, contact_info } = organizations?.[0] ?? {};

  return {
    type: 'FACILITY' as const,
    npi,
    facility: {
      name,
      address,
      contact_info,
    },
  };
};
